import Axios from "axios";
import { QuestionOrderType } from "../QuestionsScreen/questions.enum";
import { environment } from "../utils/environment";

export class AnsweringClient {
  private readonly baseURL = environment.apiBaseUrl;

  constructor(private readonly requester = Axios) {}

  public authenticate(authenticationToken: string) {
    const path = "api/v1/users";
    const data = {
      authenticationToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data);
  }

  public authenticateAnon(authenticationToken: string) {
    const path = "api/v1/users/anon";
    const data = {
      authenticationToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data);
  }

  public refreshToken(refreshToken: string) {
    const path = "api/v1/users/refresh";
    const data = {
      refreshToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data);
  }

  public assignSeduc(year: number, accessToken: string) {
    const path = `api/v1/instruments/assignment/seduc_instruments`;

    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, { year }, options);
  }

  public assignParceiros(year: number, accessToken: string) {
    const path = `api/v1/instruments/assignment/parceiros_instruments`;

    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, { year }, options);
  }

  public assignDoutorado(collectionCode: string | null, accessToken: string) {
    const path = `api/v1/instruments/assignment/doutorado_instruments`;

    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, { collectionCode }, options);
  }

  public selfInvite(selfInviteToken: string, accessToken: string) {
    const path = "api/v1/users/selfInvite";
    const data = {
      selfInviteToken,
    };
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public authAnon(anonToken: string) {
    const path = "api/v1/users/authAnon";
    const data = {
      anonToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post<{ accessToken: string; refreshToken: string }>(
      fullPath,
      data
    );
  }

  public authCode(invitationUid: string, code: string) {
    const path = "api/v1/users/authCode";
    const data = {
      invitationUid,
      code,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post<{ accessToken: string }>(fullPath, data);
  }

  public generateAnonToken(genericAnonToken: string) {
    const path = "api/v1/users/generateAnonInviteToken";

    const data = {
      genericAnonToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post<{ anonToken: string }>(fullPath, data);
  }

  public getAssignedInstruments(accessToken: string) {
    const path = "api/v1/instruments/assigned";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public getInstrumentToken(accessToken: string, assignemntId: string) {
    const path = `api/v1/instruments/assignment/${assignemntId}/token`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public getInstrumentMetadata(accessToken: string, assignmentToken: string) {
    const path = `api/v1/instruments/metadata`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
        "Instrument-Token": assignmentToken,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public getConsentTerm(accessToken: string, assignmentToken: string) {
    const path = `api/v1/instruments/consent`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
        "Instrument-Token": assignmentToken,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public answerConsent(
    accessToken: string,
    assignmentToken: string,
    consentAnswer: boolean
  ) {
    const path = `api/v1/instruments/consent`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
        "Instrument-Token": assignmentToken,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, { accepted: consentAnswer }, options);
  }

  public getNextQuestion(
    accessToken: string,
    assignmentToken: string,
    rawOrder: QuestionOrderType | string
  ) {
    const path = `api/v1/instruments/questions/${rawOrder}`;
    const headers = {
      authorization: `Bearer ${accessToken}`,
      "Instrument-Token": assignmentToken,
    };
    return this.requester.get(`${this.baseURL}${path}`, { headers });
  }

  public sendAnswer(
    accessToken: string,
    assignmentToken: string,
    questionOrder: number,
    answerOrders: number[]
  ) {
    const path = `api/v1/instruments/questions/${questionOrder}/answer`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
        "Instrument-Token": assignmentToken,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, { options: answerOrders }, options);
  }

  public sendFreeTextAnswer(
    accessToken: string,
    assignmentToken: string,
    questionOrder: number,
    text: string
  ) {
    const path = `api/v1/instruments/questions/${questionOrder}/answer/freeText`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
        "Instrument-Token": assignmentToken,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, { text }, options);
  }

  public sendTaskAnswer(
    accessToken: string,
    assignmentToken: string,
    questionOrder: number,
    taskAnswer: object
  ) {
    const path = `api/v1/instruments/questions/${questionOrder}/answer/task`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
        "Instrument-Token": assignmentToken,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(
      fullPath,
      { task_answer: taskAnswer },
      options
    );
  }

  public getAllowFeedback(accessToken: string, applicationId: number) {
    const path = `api/v1/instruments/${applicationId}/allowFeedback`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public getAssignmentFeedbackToken(accessToken: string, assignmentId: number) {
    const path = `api/v1/admin/assignments/${assignmentId}/feedbackToken`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: {
        type: "respondent",
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public sendAudioAnswer(
    accessToken: string,
    assignmentToken: string,
    questionOrder: number,
    audioBlob: Blob
  ) {
    const path = `api/v1/instruments/questions/${questionOrder}/answer/audio`;
    const fullPath = `${this.baseURL}${path}`;

    const formData = new FormData();
    formData.append("audio", audioBlob, "audio.webm");

    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
        "Instrument-Token": assignmentToken,
        "Content-Type": "multipart/form-data",
      },
    };

    return this.requester.post(fullPath, formData, options);
  }
}
